
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Resource } from '@/contexts/ResourcesContext';
import { Card, CardContent, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FileText, Link as LinkIcon, Video, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useResources } from '@/hooks/useResourceContext';
import ResourceCardImage from './ResourceCardImage';
import { getDefaultImageForType } from '@/utils/resourceUtils';

interface ResourceCardProps {
  resource: Resource;
}

const ResourceCard: React.FC<ResourceCardProps> = ({ resource }) => {
  const navigate = useNavigate();
  const { isAdmin, updateResource } = useResources();

  const handleImageUpdate = (newImageUrl: string) => {
    updateResource(resource.id, { imageUrl: newImageUrl });
    toast.success("Resource image updated successfully");
  };

  const getTypeIcon = () => {
    switch (resource.type) {
      case 'video': return <Video className="h-5 w-5 text-brand-blue" />;
      case 'document': return <FileText className="h-5 w-5 text-brand-blue" />;
      case 'link': 
      default: return <LinkIcon className="h-5 w-5 text-brand-blue" />;
    }
  };
  
  return (
    <Card 
      className="h-full hover:shadow-lg transition-shadow cursor-pointer overflow-hidden flex flex-col"
      onClick={() => navigate(`/resource/${resource.id}`)}
    >
      <ResourceCardImage 
        resource={resource} 
        isAdmin={isAdmin} 
        onImageUpdate={handleImageUpdate} 
      />
      
      <CardContent className="p-4 flex-1">
        <div className="flex items-center mb-2">
          {getTypeIcon()}
          <h3 className="text-lg font-semibold ml-2">{resource.title}</h3>
        </div>
        <CardDescription className="text-gray-600">{resource.description}</CardDescription>
      </CardContent>
      
      <CardFooter className="p-4 pt-0">
        <Button 
          variant="outline" 
          className="w-full text-brand-blue border-brand-blue hover:bg-brand-blue hover:text-white"
          onClick={(e) => {
            e.stopPropagation();
            window.open(resource.link, '_blank');
          }}
        >
          <LinkIcon className="mr-2 h-4 w-4" /> View Resource
        </Button>
      </CardFooter>
    </Card>
  );
};

export default ResourceCard;
